import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import Button from "components/v2/Button/Button";
import Dropdown from "components/v2/Dropdown/Dropdown";
import CancelApprovalRequestDialog from "pages/shipper/ShipmentQuotes/components/CancelApprovalRequestDialog";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { HandleClickRequestApprovalParams } from "../hooks/useApprovalFlowDialogs";

interface QuotationFooterApprovalRequestActionsProps {
  showAddAdditionalApprovers?: boolean;
  showCancelApprovalRequest?: boolean;
  quotation: any;
  handleClickRequestApproval: (params: HandleClickRequestApprovalParams) => void;
}

export default function QuotationFooterApprovalRequestActions({
  showAddAdditionalApprovers,
  showCancelApprovalRequest,
  quotation,
  handleClickRequestApproval,
}: QuotationFooterApprovalRequestActionsProps) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const isDetailDrawerOpen = searchParams.get("detail") !== null;

  const [showCancelApprovalRequestDialog, setShowCancelApprovalRequestDialog] = useState(false);

  const addApproversLabel = intl.formatMessage({
    id: "shipment-quotes.components.quotes.addApprovers",
    defaultMessage: "Add approvers",
  });

  const cancelApprovalRequestLabel = intl.formatMessage({
    id: "shipment-quotes.components.quotes.cancelApprovalRequest",
    defaultMessage: "Cancel approval request",
  });

  const handleClickAddApprovers = () => {
    if (handleClickRequestApproval) {
      handleClickRequestApproval({ isAdditionalApproval: true });
    }
  };

  const handleClickCancelApprovalRequest = () => {
    setShowCancelApprovalRequestDialog(true);
  };

  const refetchQuotationList = () => {
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentQuotationActionHistoryDetails(id!) });
  };

  const kebabMenuOptions = [
    {
      value: "add_approvers",
      label: addApproversLabel,
      onClick: handleClickAddApprovers,
    },

    {
      value: "cancel_approval_request",
      label: cancelApprovalRequestLabel,
      onClick: handleClickCancelApprovalRequest,
      variant: "error" as const,
    },
  ];

  return (
    <>
      <CancelApprovalRequestDialog
        isOpen={showCancelApprovalRequestDialog}
        close={() => {
          setShowCancelApprovalRequestDialog(false);
        }}
        quotationId={quotation?.reference}
        fetchQuoteRequestData={refetchQuotationList}
      />

      {showAddAdditionalApprovers && !showCancelApprovalRequest && (
        <Button label={addApproversLabel} variant="secondary-color" onClick={handleClickAddApprovers} />
      )}

      {!showAddAdditionalApprovers && showCancelApprovalRequest && (
        <Button label={cancelApprovalRequestLabel} variant="error" onClick={handleClickCancelApprovalRequest} />
      )}

      {showAddAdditionalApprovers && showCancelApprovalRequest && (
        <Dropdown
          options={kebabMenuOptions}
          placeholder={intl.formatMessage({
            id: "shipment-quotes.components.quotes.manageApprovalRequest",
            defaultMessage: "Manage approval request",
          })}
          variant="secondary-color"
          textTertiary={false}
          expandAbove={isDetailDrawerOpen}
        />
      )}
    </>
  );
}

QuotationFooterApprovalRequestActions.defaultProps = {
  showAddAdditionalApprovers: false,
  showCancelApprovalRequest: false,
};
